import { GET_CONVERSATION_INFO, GET_CONVERSATIONS } from '@/api';
import { ChatMessagesContext } from '@/context';
import {
	ChatMessagesProps,
	ChatRole,
	ChatAIStreamRequest,
	CaseStatusFilter,
} from '@/interfaces';
import { formatErrorMessage, parserUserMessage } from '@/lib';
import { getChatErrorMessage } from '@/lib/chatErrorMessage';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import { useSession } from 'next-auth/react';
import { chatAIStreamService } from '@/api/chatAIStream';
import * as React from 'react';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ConversationList } from '@/interfaces/inbox';
import { inboxService } from '@/api/inbox';
import Icons from '@/const/icons';
import { useNotification } from './useNotification';
import { useEventSource } from './useEventSource';
import { FieldValues, useForm } from 'react-hook-form';
import { END_STATUSES_CASE } from '@/const/cases';

export const useChatBoxClient = () => {
	const router = useRouter();
	const { id: conversationId } = router.query;
	const isChatAI = true;
	const containerRef = React.useRef<any>(null);
	const { toastError } = useNotification();
	const [isLoading, setIsLoading] = React.useState(false);
	const form = useForm();
	const { data: session } = useSession();
	const userId = session?.user.user.id;
	const user = session?.user.user;

	const { chatMessages, setChatMessages } =
		React.useContext(ChatMessagesContext);

	const { data: eventResponse } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/new-message/subscribe?token=${session?.accessToken}`,
	});

	const [streamingMessageId, setStreamingMessageId] = React.useState<
		string | null
	>(null);
	const [currentBotMessage, setCurrentBotMessage] = React.useState('');
	const [threadId, setThreadId] = React.useState<string | null>(null);

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<ConversationList> => {
		if (!conversationId || conversationId === 'undefined') {
			return {
				items: [],
				total: 0,
				totalItems: 0,
				totalPages: 0,
				page: 1,
				limit: 100,
			};
		}
		const response = await inboxService.getConversations({
			caseId: '',
			clientId: '',
			conversationId: conversationId as string,
			page: pageParam,
			limit: 100,
		});
		return response.data;
	};

	const {
		data: conversationPaginated,
		isLoading: isLoadingConversation,
		refetch,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CONVERSATIONS, conversationId],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		enabled: !!conversationId && conversationId !== 'undefined',
	});

	const { data: conversationInfo } = useQuery({
		queryKey: [GET_CONVERSATION_INFO, conversationId],
		queryFn: () =>
			inboxService.getConversationInfo((conversationId as string) || ''),
		enabled: !!conversationId,
		refetchOnWindowFocus: false,
	});

	const conversationMessages: ChatMessagesProps[] = React.useMemo(() => {
		return (
			conversationPaginated?.pages
				.flatMap((page) => page.items)
				.map((message) => {
					const role = message.role as ChatRole;
					return {
						message: message.content,
						role: role,
						timestamp: message.createdAt,
						id: message.id,
						first_name: message.firstname,
						last_name: message.lastname,
						profile_picture:
							message.role === 'bot'
								? Icons.hatAvatar
								: message.profile_picture,
						action_required_id: message.action_required_id,
						case_id: message.case_id,
						document_url: message.document_url,
						document_name: message.document_name,
						action_required_complete: message.action_required_complete,
						type: message.type,
					};
				}) || []
		);
	}, [conversationPaginated]);

	const scrollToBottom = React.useCallback(() => {
		const container = containerRef.current;
		if (container) {
			const tryScroll = () => {
				container.scrollTo({
					top: container.scrollHeight,
					behavior: 'smooth',
				});
			};

			tryScroll();
			setTimeout(tryScroll, 150);
			setTimeout(tryScroll, 400);
		}
	}, []);

	React.useEffect(() => {
		if (eventResponse && conversationId && conversationId !== 'undefined') {
			refetch();
			scrollToBottom();
		}
	}, [eventResponse, refetch, conversationId]);

	React.useEffect(() => {
		if (userId) {
			const savedThreadId = getLocalStorage(`threadId_${userId}`);
			if (savedThreadId) {
				setThreadId(savedThreadId);
			}
		}
	}, [userId]);

	// Scroll to bottom on initial message load or when messages change
	React.useEffect(() => {
		if (chatMessages && chatMessages.length > 0 && !isLoadingConversation) {
			scrollToBottom();
		}
	}, [chatMessages, isLoadingConversation, conversationId, scrollToBottom]);

	React.useEffect(() => {
		uploadChat();
	}, [conversationMessages]);

	const handleStreamingResponse = async (
		message: string,
		messagesAux: ChatMessagesProps[],
		currentThreadId: string | null,
		actionRequiredId?: number,
		documentUrl?: string
	): Promise<void> => {
		try {
			const botMessageId = (Date.now() + 1).toString();
			setStreamingMessageId(botMessageId);
			setCurrentBotMessage('');

			const timestamp = format(new Date(), 'MM/dd/yyyy, h:mm a');

			const initialBotMessage: ChatMessagesProps = {
				message: '',
				role: 'bot',
				timestamp,
				id: botMessageId,
				profile_picture: Icons.hatAvatar,
				first_name: 'ClaimMagic',
				last_name: 'AI',
			};

			let currentMessages = [...messagesAux, initialBotMessage];
			setChatMessages(currentMessages);

			let requestBody: ChatAIStreamRequest;

			if (messagesAux.length === 0) {
				requestBody = {
					content: '', // empty because the backend will send the greeting message
					application_id: String(conversationInfo?.data.applicationId),
					inbox: true,
					needs_greeting: true,
				};
			} else {
				requestBody = {
					content: message,
					application_id: String(conversationInfo?.data.applicationId),
					conversation_id: (conversationId as string) || '',
					inbox: true,
				};

				if (conversationInfo?.data.case.id) {
					requestBody.case_id = conversationInfo.data.case.id;
				}
				if (currentThreadId) {
					requestBody.thread_id = currentThreadId;
				}
				if (actionRequiredId) {
					requestBody.action_required_id = actionRequiredId;
				}
				if (documentUrl) {
					requestBody.document_url = documentUrl;
				}
			}

			await chatAIStreamService.streamChatMessage(requestBody, {
				onStart: (data) => {
					if (data.thread_id) {
						setThreadId(data.thread_id);
						setLocalStorage(`threadId_${userId}`, data.thread_id);
					}
				},
				onContent: (accumulated) => {
					setCurrentBotMessage(accumulated);

					const updatedMessages = currentMessages.map((msg) =>
						msg.id === botMessageId ? { ...msg, message: accumulated } : msg
					);

					currentMessages = updatedMessages;
					setChatMessages(updatedMessages);
				},
				onDone: (finalData) => {
					if (finalData?.thread_id) {
						setThreadId(finalData.thread_id);
						setLocalStorage(`threadId_${userId}`, finalData.thread_id);
					}

					const finalMessages = currentMessages.map((msg) =>
						msg.id === botMessageId
							? {
									...msg,
									message:
										finalData.fullMessage +
										`<div class="chat-timestamp"><span>${timestamp}</span></div>`,
							  }
							: msg
					);

					setChatMessages(finalMessages);
					setStreamingMessageId(null);
					setCurrentBotMessage('');
				},
				onError: (err) => {
					console.error('Streaming error:', err);
					throw err;
				},
			});
		} catch (error) {
			console.error('Streaming error (outer catch):', error);
			throw error;
		}
	};

	const addChatMessages = async (
		message: string,
		role: ChatRole,
		customThreadId?: string | null,
		actionRequiredId?: number,
		documentUrl?: string
	): Promise<void> => {
		setIsLoading(true);
		const messagesAux: ChatMessagesProps[] = [...chatMessages];

		try {
			if (role === 'user') {
				messagesAux.push({
					message: parserUserMessage(message, 'user'),
					role: role,
					profile_picture: user?.profile_picture,
					first_name: user?.firstname,
					last_name: user?.lastname,
					timestamp: new Date().toISOString(),
				});
				setChatMessages(messagesAux);
			}

			const currentThreadId =
				customThreadId !== undefined ? customThreadId : threadId;

			await handleStreamingResponse(
				message,
				messagesAux,
				currentThreadId,
				actionRequiredId,
				documentUrl
			);
		} catch (error) {
			console.error({ error });
			const chatError = getChatErrorMessage();
			messagesAux.push(chatError);
			setChatMessages(messagesAux);
		} finally {
			setIsLoading(false);
			setStreamingMessageId(null);
			setCurrentBotMessage('');
		}
	};

	const uploadChat = () => {
		setChatMessages(conversationMessages);

		const savedThreadId = getLocalStorage(`threadId_${userId}`);
		if (savedThreadId) {
			setThreadId(savedThreadId);
		}
	};

	const onSubmit = async (data: FieldValues): Promise<void> => {
		try {
			addChatMessages(data.message, 'user');
			scrollToBottom();
			form.reset();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const inputDisabled =
		END_STATUSES_CASE.includes(
			conversationInfo?.data?.case.progress_name as CaseStatusFilter
		) ||
		END_STATUSES_CASE.includes(
			conversationInfo?.data.progress_applicant as CaseStatusFilter
		);

	// const removeButtonMessages = (currentMessages: ChatMessagesProps[]) => {
	// 	const auxChatMessages = currentMessages;
	// 	const index = auxChatMessages.length - 1;

	// 	const messageSplitted = auxChatMessages[index].message.split(
	// 		'<div className="grid grid-cols-2 gap-3">'
	// 	);

	// 	auxChatMessages[index].message = messageSplitted[0];
	// 	return auxChatMessages;
	// };

	return {
		chatMessages,
		addChatMessages,
		isLoading,
		// setIsLoading,
		// removeButtonMessages,
		streamingMessageId,
		currentBotMessage,
		threadId,
		isChatAI,
		user,
		conversationInfo,
		applicationId: conversationInfo?.data.applicationId,
		caseId: conversationInfo?.data.case.id,
		conversationId: conversationInfo?.data.id,
		onSubmit,
		containerRef,
		form,
		inputDisabled,
		isLoadingConversation,
		conversationPaginated,
	};
};
